import { get, set, remove, push, update } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { firebase } from '../config/configureFirebase';

// Driver ringtones — the incoming-booking alert sound a driver can pick in
// Profile › Incoming booking sound (DRIVER_RINGTONES.md).
//
//   Storage  ringtones/{id}                     create-only (staff claim), audio/*, ≤ 1 MB
//   RTDB     /ringtones/{id} = { name, url, bytes, type, ext, sec, at, by }
//            read: any signed-in user (one-shot get when the picker opens,
//            never a listener); write: admin
//
//   RTDB     /ringtone_default = { id, name?, url?, bytes?, ext?, sec?, overrideAt?, at, by }
//            the sound the office set for every driver (Settings › Ringtones).
//            read: any signed-in user (a phone reads this tiny node at start-up
//            and at most hourly, never a listener); write: admin. Absent = Classic.
//            A new overrideAt also takes away what drivers picked themselves, once.
//
// Six sounds come with the app, so nothing is read or downloaded for them, in this
// order (owner, 3 Nov 2026): Classic (the bundled sound the app always had), the
// four new_order sounds and Uplifting bells.
// A driver who picks a clip, or whose default is a clip, downloads it ONCE to the
// phone's document directory and the alert plays that local copy.

export const RINGTONE_MAX_BYTES = 1024 * 1024;
export const RINGTONE_MAX_SECONDS = 30;
export const RINGTONE_MAX_COUNT = 12;
export const RINGTONE_NAME_MAX = 40;
export const RINGTONE_CLASSIC_ID = 'classic';
export const RINGTONE_NEW_ORDER_1_ID = 'new_order_1';
export const RINGTONE_NEW_ORDER_2_ID = 'new_order_2';
export const RINGTONE_NEW_ORDER_3_ID = 'new_order_3';
export const RINGTONE_NEW_ORDER_4_ID = 'new_order_4';
export const RINGTONE_UPLIFTING_BELLS_ID = 'uplifting_bells';
const BUILT_IN_IDS = [RINGTONE_CLASSIC_ID, RINGTONE_NEW_ORDER_1_ID, RINGTONE_NEW_ORDER_2_ID, RINGTONE_NEW_ORDER_3_ID, RINGTONE_NEW_ORDER_4_ID, RINGTONE_UPLIFTING_BELLS_ID];
// Formats every driver phone plays (Android ExoPlayer and iOS AVPlayer).
// No Ogg: older iPhones cannot decode it, so a driver could pick a clip that
// never sounds.
export const RINGTONE_TYPES = {
  'audio/mpeg': 'mp3', 'audio/mp3': 'mp3',
  'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/wave': 'wav', 'audio/vnd.wave': 'wav',
  'audio/mp4': 'm4a', 'audio/x-m4a': 'm4a', 'audio/m4a': 'm4a', 'audio/aac': 'aac', 'audio/x-aac': 'aac',
};
// Content type stored with the object — always derived from the validated
// extension: the Storage rule requires audio/*, and browsers label the same
// file differently (Firefox, an unregistered Windows association …).
export const RINGTONE_MIME = { mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', aac: 'audio/aac' };
export const RINGTONE_ACCEPT = 'audio/mpeg,audio/wav,audio/mp4,audio/aac,.mp3,.wav,.m4a,.aac';

const extOf = (file) => {
  if (file && RINGTONE_TYPES[file.type]) return RINGTONE_TYPES[file.type];
  const m = /\.([a-z0-9]{2,4})$/i.exec(String((file && file.name) || ''));
  const ext = m ? m[1].toLowerCase() : null;
  return ext && RINGTONE_MIME[ext] ? ext : null;
};

// Sorted list { id, name, url, bytes, type, ext, at, by } — never throws.
export const fetchRingtones = async () => {
  const { ringtonesRef } = firebase;
  try {
    const snap = await get(ringtonesRef);
    const data = snap.val() || {};
    const list = Object.keys(data)
      .filter((id) => id.charAt(0) !== '_' && data[id] && typeof data[id] === 'object' && typeof data[id].url === 'string' && typeof data[id].name === 'string')
      .map((id) => Object.assign({ id }, data[id]))
      .sort((a, b) => (Number(a.at) || 0) - (Number(b.at) || 0));
    return { success: true, list };
  } catch (e) {
    return { success: false, error: (e && e.message) || 'read failed', list: [] };
  }
};

export const ringtoneExt = extOf;

// { success, row }: row null when the office never set a default. Never throws.
export const fetchRingtoneDefault = async () => {
  const { ringtoneDefaultRef } = firebase;
  try {
    const snap = await get(ringtoneDefaultRef);
    const v = snap.val();
    return { success: true, row: v && typeof v === 'object' && typeof v.id === 'string' ? v : null };
  } catch (e) {
    return { success: false, error: (e && e.message) || 'read failed', row: null };
  }
};

// Admin: this sound (a row of /ringtones, or { id } of one that comes with the
// app) becomes the default for all drivers. override: also takes away the sound
// drivers picked themselves. A merge, so a later change without it keeps the
// last overrideAt for a phone that has not seen it yet.
export const setRingtoneDefault = async (row, opts) => {
  const { ringtoneDefaultRef } = firebase;
  const { override, who } = opts || {};
  const clip = BUILT_IN_IDS.indexOf(row.id) < 0;
  const now = Date.now();
  const patch = {
    id: row.id,
    name: clip ? row.name : null,
    url: clip ? row.url : null,
    bytes: clip ? (Number(row.bytes) || null) : null,
    ext: clip ? (row.ext || null) : null,
    sec: clip ? (Number(row.sec) || null) : null,
    at: now,
    by: who || null,
  };
  if (override) patch.overrideAt = now;
  await update(ringtoneDefaultRef, patch);
  return patch;
};

// Admin: upload the clip, then publish the row (the row is written LAST so a
// phone can never list a clip whose file is still uploading). `seconds` is
// the length the portal measured by decoding the file in the browser
// (RingtoneSettings.js refuses files it cannot play). Throws with a message
// the portal shows as-is.
export const uploadRingtone = async (name, file, who, seconds) => {
  const { ringtonesRef, ringtoneRef, ringtoneFileRef } = firebase;
  if (!file) throw new Error('Choose an audio file.');
  const ext = extOf(file);
  if (!ext) throw new Error('Choose an mp3, wav, m4a or aac file.');
  if (file.size > RINGTONE_MAX_BYTES) throw new Error('Keep the file under 1 MB — a 5 to 20 second clip.');
  const cleanName = String(name || file.name || '').replace(/\.[a-z0-9]{2,4}$/i, '').trim().slice(0, RINGTONE_NAME_MAX);
  if (!cleanName) throw new Error('Give the ringtone a name.');
  const id = push(ringtonesRef).key;
  const r = ringtoneFileRef(id);
  const contentType = RINGTONE_MIME[ext];
  await uploadBytesResumable(r, file, { contentType });
  const url = await getDownloadURL(r);
  const row = { name: cleanName, url, bytes: file.size || 0, type: contentType, ext, at: Date.now(), by: who || null };
  if (Number.isFinite(Number(seconds)) && Number(seconds) > 0) row.sec = Math.round(Number(seconds) * 10) / 10;
  await set(ringtoneRef(id), row);
  return Object.assign({ id }, row);
};

// Admin: removes the row, so no phone offers it any more. The audio object
// stays in Storage on purpose — Storage is create-only for clients (the
// `admin` claim is the shared staff claim, see json/storage.rules), and a
// driver who picked the clip can still re-download it after a reinstall.
export const deleteRingtone = async (id) => {
  const { ringtoneRef } = firebase;
  await remove(ringtoneRef(id));
  return { success: true };
};
